/**
 * Global delayed inbox send: countdown and undo survive chat switches and other dashboard routes.
 *
 * Where: wraps `DashboardLayout` so pending sends keep running when leaving `/inbox`.
 * Uses: `chatApi.sendReply`, React Query `useMutation`; `INBOX_SEND_DELAY_SECONDS` from `inboxSendDelay`.
 * Behavior: one pending send at a time; bottom bar with countdown + Undo; optional bridge from `Inbox` to merge API response into selected chat.
 * Invariants: `useInboxPendingSend` throws if used outside `InboxPendingSendProvider`; only one queued send globally.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { chatApi } from '@/api/chat';
import type { Chat } from '@/types/chat';
import {
  INBOX_SEND_DELAY_SECONDS,
  type InboxPendingSendState,
} from './inboxSendDelay';

type SendSuccessHandler = (updatedChat: Chat, chatId: string) => void;

type InboxPendingSendContextValue = {
  pendingSend: InboxPendingSendState | null;
  queueSend: (
    message: string,
    chatId: string,
    opts?: { fromDraft?: boolean; chatLabel?: string }
  ) => void;
  cancelPendingSend: () => void;
  /** True while the delayed send is posting to the API. */
  isSending: boolean;
  /** Composer for this chat should be disabled (message is queued for this thread). */
  isPendingForChat: (chatId: string) => boolean;
  /** Hide AI draft for this conversation after a draft-originated send completes. */
  draftSuppressedForChat: (chatId: string) => boolean;
  /**
   * Call when the inbox conversation selection changes (any `selectedChat.key` change).
   * Clears draft-suppression state to match previous per-conversation behavior.
   */
  notifyInboxConversationChanged: () => void;
  /** Inbox page registers so `sendReply` success can update `selectedChat` when still viewing that thread. */
  setSendSuccessHandler: (handler: SendSuccessHandler | null) => void;
};

const InboxPendingSendContext = createContext<InboxPendingSendContextValue | null>(
  null
);

export function useInboxPendingSend(): InboxPendingSendContextValue {
  const ctx = useContext(InboxPendingSendContext);
  if (!ctx) {
    throw new Error('useInboxPendingSend must be used within InboxPendingSendProvider');
  }
  return ctx;
}

export const InboxPendingSendProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const [pendingSend, setPendingSend] = useState<InboxPendingSendState | null>(null);
  const [draftSuppressedChatId, setDraftSuppressedChatId] = useState<string | null>(
    null
  );
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const sendSuccessHandlerRef = useRef<SendSuccessHandler | null>(null);
  const setSendSuccessHandler = useCallback((handler: SendSuccessHandler | null) => {
    sendSuccessHandlerRef.current = handler;
  }, []);

  const sendMessageMutation = useMutation({
    mutationFn: ({ chatId, message }: { chatId: string; message: string }) =>
      chatApi.sendReply(chatId, { content: message }),
    onSuccess: (updatedChat, { chatId }) => {
      sendSuccessHandlerRef.current?.(updatedChat, chatId);
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      queryClient.invalidateQueries({ queryKey: ['chat', chatId] });
      setToast({
        open: true,
        message: 'Message sent successfully!',
        severity: 'success',
      });
    },
    onError: () => {
      setToast({
        open: true,
        message: 'Failed to send message. Please try again.',
        severity: 'error',
      });
    },
  });

  const onCommitSendRef = useRef(sendMessageMutation.mutate);
  onCommitSendRef.current = sendMessageMutation.mutate;

  useEffect(() => {
    if (pendingSend === null) return;

    if (pendingSend.countdown <= 0) {
      const { message, chatId, fromDraft } = pendingSend;
      setPendingSend(null);
      if (fromDraft) {
        setDraftSuppressedChatId(chatId);
      }
      onCommitSendRef.current({ chatId, message });
      return;
    }

    const timer = window.setTimeout(() => {
      setPendingSend((prev) =>
        prev ? { ...prev, countdown: prev.countdown - 1 } : null
      );
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [pendingSend]);

  const queueSend = useCallback(
    (
      message: string,
      chatId: string,
      opts?: { fromDraft?: boolean; chatLabel?: string }
    ) => {
      if (!chatId || sendMessageMutation.isPending) return;
      setPendingSend((prev) => {
        if (prev !== null) return prev;
        return {
          message,
          countdown: INBOX_SEND_DELAY_SECONDS,
          chatId,
          fromDraft: opts?.fromDraft,
          chatLabel: opts?.chatLabel,
        };
      });
    },
    [sendMessageMutation.isPending]
  );

  const cancelPendingSend = useCallback(() => {
    setPendingSend(null);
  }, []);

  const notifyInboxConversationChanged = useCallback(() => {
    setDraftSuppressedChatId(null);
  }, []);

  const isPendingForChat = useCallback(
    (chatId: string) => pendingSend !== null && pendingSend.chatId === chatId,
    [pendingSend]
  );

  const draftSuppressedForChat = useCallback(
    (chatId: string) => draftSuppressedChatId === chatId,
    [draftSuppressedChatId]
  );

  const value = useMemo(
    () => ({
      pendingSend,
      queueSend,
      cancelPendingSend,
      isSending: sendMessageMutation.isPending,
      isPendingForChat,
      draftSuppressedForChat,
      notifyInboxConversationChanged,
      setSendSuccessHandler,
    }),
    [
      pendingSend,
      queueSend,
      cancelPendingSend,
      sendMessageMutation.isPending,
      isPendingForChat,
      draftSuppressedForChat,
      notifyInboxConversationChanged,
      setSendSuccessHandler,
    ]
  );

  const pending = pendingSend;
  const countdownProgress = pending
    ? (pending.countdown / INBOX_SEND_DELAY_SECONDS) * 100
    : 0;

  return (
    <InboxPendingSendContext.Provider value={value}>
      {children}
      <Snackbar
        open={pending !== null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          bottom: { xs: 16, sm: 24 },
          zIndex: (theme) => theme.zIndex.snackbar,
        }}
      >
        {pending ? (
          <Paper
            elevation={8}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.25,
              maxWidth: 'min(560px, calc(100vw - 32px))',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.22),
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
              borderRadius: 2,
            }}
          >
            <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
              <CircularProgress
                size={32}
                variant="determinate"
                value={countdownProgress}
                color="primary"
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, fontSize: '0.65rem', lineHeight: 1 }}
                >
                  {pending.countdown}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary" noWrap>
                Sending{pending.chatLabel ? ` to ${pending.chatLabel}` : ''}
                …
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                noWrap
              >
                {pending.message.length > 80
                  ? `${pending.message.substring(0, 80)}…`
                  : pending.message}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={cancelPendingSend}
              sx={{ flexShrink: 0 }}
            >
              Undo
            </Button>
          </Paper>
        ) : (
          <span />
        )}
      </Snackbar>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </InboxPendingSendContext.Provider>
  );
};
